import { adjudicationService } from '../../helpers/authservice/adjudication.service';

// Laravel answers with either {message} or {message, errors: {field: [...]}}, and
// a handler that assumes one shape throws inside its own catch — the bug that
// left change-password stuck on a spinner. Flatten whichever arrives.
const errorMessages = (error) => {
    // No response at all — timeout, dropped connection, CORS. Worth saying so
    // plainly: for an adjudication action the adjudicator has to know the write
    // may or may not have landed before they repeat it.
    if (error && !error.response) {
        return [error.code === 'ECONNABORTED'
            ? 'The server did not answer in time. Reload the contest to check whether this was saved before trying again.'
            : 'We could not reach the server. Check your connection and try again.'];
    }

    const data = (error && error.response && error.response.data) || {};

    if (data.errors) {
        return Object.values(data.errors).flat();
    }

    return [data.message || 'Something went wrong. Please try again.'];
};

export const state = {
    tickets: [],
    pagination: {
        per_page: '',
        total: '',
        page: ''
    },
    summary: {},
    ticket: null,
    lift_reasons: []
};

export const mutations = {
    FETCH_TICKETS: (state, payload) => {
        state.tickets = payload;
    },
    FETCH_PAGINATION: (state, payload) => {
        state.pagination = payload;
    },
    FETCH_SUMMARY: (state, payload) => {
        state.summary = payload;
    },
    FETCH_TICKET: (state, payload) => {
        state.ticket = payload;
    },
    FETCH_LIFT_REASONS: (state, payload) => {
        state.lift_reasons = payload;
    }
};

export const actions = {
    getTickets: ({ commit, dispatch }, query) => {
        dispatch('globals/loading', null, { root: true });

        return adjudicationService.getTickets(query)
            .then(({ data, meta }) => {
                commit('FETCH_TICKETS', data);
                commit('FETCH_PAGINATION', {
                    page: meta.current_page,
                    total: meta.total,
                    per_page: meta.per_page
                });
                dispatch('globals/loaded', null, { root: true });
            })
            .catch((error) => {
                errorMessages(error).forEach((message) => {
                    dispatch('notification/error', message, { root: true });
                });
                dispatch('globals/loaded', null, { root: true });
            });
    },

    getSummary: ({ commit, dispatch }) => {
        return adjudicationService.getSummary()
            .then(({ data }) => commit('FETCH_SUMMARY', data))
            .catch((error) => {
                errorMessages(error).forEach((message) => {
                    dispatch('notification/error', message, { root: true });
                });
            });
    },

    getTicket: ({ commit, dispatch }, id) => {
        dispatch('globals/loading', null, { root: true });

        return adjudicationService.getTicket(id)
            .then(({ data }) => {
                commit('FETCH_TICKET', data);
                dispatch('globals/loaded', null, { root: true });
                return data;
            })
            .catch((error) => {
                errorMessages(error).forEach((message) => {
                    dispatch('notification/error', message, { root: true });
                });
                dispatch('globals/loaded', null, { root: true });
                throw error;
            });
    },

    getLiftReasons: ({ commit }) => {
        return adjudicationService.getLiftReasons()
            .then(({ data }) => commit('FETCH_LIFT_REASONS', data))
            .catch(() => commit('FETCH_LIFT_REASONS', []));
    },

    /*
     * Every mutating action returns the whole refreshed ticket, so each commits
     * the response rather than re-fetching. The rethrow matters: the views await
     * these to decide whether to close a modal, and a swallowed rejection would
     * leave a failed action looking like it worked.
     */

    setStatus: ({ commit, dispatch }, { id, status }) => {
        return mutate(commit, dispatch, () => adjudicationService.setStatus(id, status));
    },

    sendMessage: ({ commit, dispatch }, { id, body, request_documents, files }) => {
        return mutate(commit, dispatch, () =>
            adjudicationService.sendMessage(id, { body, request_documents, files }));
    },

    uploadDocuments: ({ commit, dispatch }, { id, files }) => {
        return mutate(commit, dispatch, () => adjudicationService.uploadDocuments(id, files));
    },

    setAppealStatus: ({ commit, dispatch }, { id, appealId, status, notes }) => {
        return mutate(commit, dispatch, () =>
            adjudicationService.setAppealStatus(id, appealId, { status, notes }));
    },

    setHearing: ({ commit, dispatch }, { id, hearing_at, hearing_venue, hearing_remarks, cancel_remarks }) => {
        return mutate(commit, dispatch, () =>
            adjudicationService.setHearing(id, { hearing_at, hearing_venue, hearing_remarks, cancel_remarks }));
    },

    resolve: ({ commit, dispatch }, { id, resolution, notes }) => {
        return mutate(commit, dispatch, () =>
            adjudicationService.resolve(id, { resolution, notes }));
    }
};

function mutate(commit, dispatch, call) {
    dispatch('globals/loading', null, { root: true });

    return call()
        .then(({ data }) => {
            commit('FETCH_TICKET', data);
            dispatch('globals/loaded', null, { root: true });
            return data;
        })
        .catch((error) => {
            errorMessages(error).forEach((message) => {
                dispatch('notification/error', message, { root: true });
            });
            dispatch('globals/loaded', null, { root: true });
            throw error;
        });
}
